import { useMemo } from "react";
import { Flex, Text, useMantineTheme } from "@mantine/core";
import { MantineReactTable, type MRT_ColumnDef } from "mantine-react-table";
import { ORDER_STATUS_OPTIONS, OrderStatusBadge, OrderTypeBadge } from "@/pages/admin/AdminStatusBadge";
import { formatINR, formatDate } from "@/shared/utils/format.utils";

const lumpsumTypeLabel = (type: string | null | undefined): string | null => {
  if (type === "P") return "LUMPSUM";
  if (type === "R") return "REDEMPTION";
  if (type === "S") return "SWITCH";
  return null;
};

const sxpInvestmentType = (sxp: any): string | null => {
  if (!sxp) return null;
  if (sxp.status === "paused") return "PAUSE";
  return sxp.sxpType ?? null; // SIP, STP, SWP, TOPUP, SPROD
};

type OrderRow = {
  id: string;
  date: string;
  amcName: string | null;
  schemeName: string;
  investmentType: string | null;
  orderStatus: string | null;
  rtaRemarks: string | null;
  transactionValue: number | null;
  folioNumber: string | null;
  allotmentAmount: number | null;
  allotmentUnits: number | null;
  allotmentNav: number | null;
  allotmentFolio: string | null;
  allotmentStampDuty: number | null;
  allotmentDate: string | null;
  redemptionDate: string | null;
  redemptionUnits: number | null;
  redemptionAmount: number | null;
  redemptionNav: number | null;
  redemptionNavDate: string | null;
  redemptionFolio: string | null;
  redemptionTds: number | null;
  redemptionExitLoad: number | null;
  redemptionStt: number | null;
  redemptionPayoutDate: string | null;
  redemptionPayoutUtr: string | null;
};

type Props = {
  orders: any[]; // lumpsums from getById (aliased as `orders`)
  sxps: any[];
};

export default function OrderLogsTab({ orders, sxps }: Props) {
  const theme = useMantineTheme();

  const rows: OrderRow[] = useMemo(() => {
    const lumpsumRows: OrderRow[] = orders.map((l) => ({
      id: `lumpsum:${l.id}`,
      date: l.createdAt,
      amcName: l.masterScheme?.scheme_amc_name ?? null,
      schemeName: l.masterScheme?.name ?? l.scheme ?? "—",
      investmentType: lumpsumTypeLabel(l.type),
      orderStatus: l.status ?? null,
      rtaRemarks: l.rtaRemarks ?? null,
      transactionValue: l.amount ?? null,
      folioNumber: l.folio ?? null,
      allotmentAmount: l.allotment_amount ?? null,
      allotmentUnits: l.allotment_units ?? null,
      allotmentNav: l.allotment_nav ?? null,
      allotmentFolio: l.allotment_folio ?? null,
      allotmentStampDuty: l.allotment_stamp_duty ?? null,
      allotmentDate: l.allotment_date ?? null,
      redemptionDate: l.redemption_date ?? null,
      redemptionUnits: l.redemption_units ?? null,
      redemptionAmount: l.redemption_amount ?? null,
      redemptionNav: l.redemption_nav ?? null,
      redemptionNavDate: l.redemption_nav_date ?? null,
      redemptionFolio: l.redemption_folio ?? null,
      redemptionTds: l.redemption_tds ?? null,
      redemptionExitLoad: l.redemption_exit_load ?? null,
      redemptionStt: l.redemption_stt ?? null,
      redemptionPayoutDate: l.redemption_payout_date ?? null,
      redemptionPayoutUtr: l.redemption_payout_utr ?? null,
    }));

    const sxpOrderRows: OrderRow[] = sxps.flatMap((sxp) => {
      const installments = sxp.orders ?? [];
      const amcName = sxp.masterScheme?.scheme_amc_name ?? null;
      const schemeName = sxp.masterScheme?.name ?? sxp.srcScheme ?? "—";
      const investmentType = sxpInvestmentType(sxp);
      return installments.map((o: any) => ({
        id: `order:${o.id}`,
        date: o.createdAt,
        amcName,
        schemeName,
        investmentType,
        orderStatus: o.status ?? null,
        rtaRemarks: o.rtaRemarks ?? null,
        transactionValue: sxp.amount ?? null,
        folioNumber: null,
        allotmentAmount: o.allotment_amount ?? null,
        allotmentUnits: o.allotment_units ?? null,
        allotmentNav: o.allotment_nav ?? null,
        allotmentFolio: o.allotment_folio ?? null,
        allotmentStampDuty: o.allotment_stamp_duty ?? null,
        allotmentDate: o.allotment_date ?? null,
        redemptionDate: o.redemption_date ?? null,
        redemptionUnits: o.redemption_units ?? null,
        redemptionAmount: o.redemption_amount ?? null,
        redemptionNav: o.redemption_nav ?? null,
        redemptionNavDate: o.redemption_nav_date ?? null,
        redemptionFolio: o.redemption_folio ?? null,
        redemptionTds: o.redemption_tds ?? null,
        redemptionExitLoad: o.redemption_exit_load ?? null,
        redemptionStt: o.redemption_stt ?? null,
        redemptionPayoutDate: o.redemption_payout_date ?? null,
        redemptionPayoutUtr: o.redemption_payout_utr ?? null,
      }));
    });

    return [...lumpsumRows, ...sxpOrderRows].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [orders, sxps]);

  const dash = <span style={{ color: "var(--mantine-color-slate-4)" }}>—</span>;

  const columns: MRT_ColumnDef<OrderRow>[] = useMemo(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatDate(cell.getValue<string>()),
      },
      {
        accessorKey: "amcName",
        header: "AMC Name",
        size: 180,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? dash,
      },
      {
        accessorKey: "schemeName",
        header: "Scheme Name",
        size: 220,
        Cell: ({ cell }) => cell.getValue<string>() || dash,
      },
      {
        accessorKey: "investmentType",
        header: "Investment type",
        size: 150,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => <OrderTypeBadge type={cell.getValue<string | null>()} />,
      },
      {
        accessorKey: "orderStatus",
        header: "Order status",
        size: 190,
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: ORDER_STATUS_OPTIONS,
          clearable: true,
        },
        enableSorting: true,
        Cell: ({ cell }) => <OrderStatusBadge status={cell.getValue<string | null>()} />,
      },
      {
        accessorKey: "rtaRemarks",
        header: "RTA Remarks",
        size: 260,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return v ? (
            <Text fz="sm" title={v} style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {v}
            </Text>
          ) : dash;
        },
      },
      {
        accessorKey: "transactionValue",
        header: "Transaction value",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatINR(cell.getValue<number | null>()),
      },
      {
        accessorKey: "folioNumber",
        header: "Folio Number",
        size: 150,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? dash,
      },
      {
        accessorKey: "allotmentAmount",
        header: "Allotment amount",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatINR(cell.getValue<number | null>()),
      },
      {
        accessorKey: "allotmentUnits",
        header: "Allotment units",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v == null ? dash : v;
        },
      },
      {
        accessorKey: "allotmentNav",
        header: "Allotment NAV",
        size: 150,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v == null ? dash : v;
        },
      },
      {
        accessorKey: "allotmentFolio",
        header: "Allotment folio",
        size: 170,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? dash,
      },
      {
        accessorKey: "allotmentStampDuty",
        header: "Allotment stamp duty",
        size: 190,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatINR(cell.getValue<number | null>()),
      },
      {
        accessorKey: "allotmentDate",
        header: "Allotment date",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return v ? formatDate(v) : dash;
        },
      },
      {
        accessorKey: "redemptionDate",
        header: "Redemption date",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return v ? formatDate(v) : dash;
        },
      },
      {
        accessorKey: "redemptionUnits",
        header: "Redemption units",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v == null ? dash : v;
        },
      },
      {
        accessorKey: "redemptionAmount",
        header: "Redemption amount",
        size: 170,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatINR(cell.getValue<number | null>()),
      },
      {
        accessorKey: "redemptionNav",
        header: "Redemption NAV",
        size: 150,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v == null ? dash : v;
        },
      },
      {
        accessorKey: "redemptionNavDate",
        header: "Redemption NAV date",
        size: 180,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return v ? formatDate(v) : dash;
        },
      },
      {
        accessorKey: "redemptionFolio",
        header: "Redemption folio",
        size: 170,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? dash,
      },
      {
        accessorKey: "redemptionTds",
        header: "Redemption TDS",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatINR(cell.getValue<number | null>()),
      },
      {
        accessorKey: "redemptionExitLoad",
        header: "Redemption exit load",
        size: 180,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatINR(cell.getValue<number | null>()),
      },
      {
        accessorKey: "redemptionStt",
        header: "Redemption STT",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatINR(cell.getValue<number | null>()),
      },
      {
        accessorKey: "redemptionPayoutDate",
        header: "Redemption payout date",
        size: 190,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return v ? formatDate(v) : dash;
        },
      },
      {
        accessorKey: "redemptionPayoutUtr",
        header: "Redemption payout UTR",
        size: 190,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? dash,
      },
    ],
    []
  );

  return (
    <Flex direction="column" gap="md">
      <Text fw={500} fz="md">
        All transactions
      </Text>

      <MantineReactTable
        columns={columns}
        data={rows}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        enableGlobalFilter={false}
        positionToolbarAlertBanner="none"
        initialState={{
          density: "xs",
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderRight: "1px solid var(--mantine-color-gray-2)",
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 400,
            fontSize: "var(--mantine-font-size-sm)",
            color: theme.colors.slate[5],
            borderRight: "1px solid var(--mantine-color-gray-2)",
            backgroundColor: "var(--mantine-color-gray-0)",
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 300px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
      />
    </Flex>
  );
}
